import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { IMAGES } from "@/lib/site";
import { EASE, MaskedLines } from "@/components/Reveal";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="home" ref={ref} className="relative min-h-screen flex flex-col justify-end overflow-hidden bg-ink" data-testid="hero-section">
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <motion.img
          src={IMAGES.hero}
          alt="Contemporary custom home designed and built by ArcView Homes"
          className="w-full h-[118%] object-cover"
          initial={{ scale: 1.12 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2.2, ease: EASE }}
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/35 to-ink/30" />
      <div className="absolute inset-0 bg-gradient-to-r from-ink/45 to-transparent" />

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-[1440px] mx-auto w-full px-5 sm:px-8 lg:px-12 pb-24 sm:pb-28 pt-40">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.15 }}
          className="eyebrow text-sky mb-6"
        >
          ArcView Homes — Tarneit, Victoria
        </motion.p>

        <MaskedLines
          animate
          delay={0.3}
          className="font-serif text-cream font-medium leading-[0.98] tracking-tight text-[clamp(2.9rem,8.5vw,7.5rem)]"
          lines={[
            <>Make Your</>,
            <><span className="italic text-gradient">Dream Home</span></>,
            <>Reality.</>,
          ]}
        />

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.9 }}
          className="mt-7 max-w-xl text-cream/85 text-[15px] sm:text-lg leading-relaxed font-light"
        >
          From concept and design to approvals, construction and handover — ArcView Homes brings
          your vision together under one roof.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 1.05 }}
          className="mt-9 flex flex-col sm:flex-row gap-4"
        >
          <a
            href="#contact"
            data-testid="hero-cta-start-dream-home"
            className="group inline-flex items-center justify-center gap-3 bg-arc text-cream font-semibold text-[15px] px-8 py-4 rounded-full hover:bg-arc-dark hover:shadow-xl hover:shadow-arc/30 transition-all duration-300"
          >
            Start Your Dream Home
            <span className="h-1.5 w-1.5 rounded-full bg-cream group-hover:translate-x-1 transition-transform duration-300" />
          </a>
          <a
            href="#projects"
            data-testid="hero-cta-explore-projects"
            className="inline-flex items-center justify-center gap-3 border border-cream/50 text-cream font-semibold text-[15px] px-8 py-4 rounded-full hover:bg-cream hover:text-ink transition-all duration-300"
          >
            Explore Our Projects
          </a>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.3 }}
          className="mt-8 font-mono text-[11px] tracking-[0.35em] uppercase text-cream/70"
        >
          Design · Approvals · Build · Handover
        </motion.p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2"
        data-testid="hero-scroll-indicator"
      >
        <span className="font-mono text-[9px] tracking-[0.4em] uppercase text-cream/60">Scroll</span>
        <span className="relative h-10 w-px bg-cream/25 overflow-hidden">
          <span className="absolute top-0 left-0 h-3 w-px bg-cream animate-scroll-dot" />
        </span>
      </motion.div>
    </section>
  );
}
